import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { iconMap } from "@/utils/tripBuilder/accommodation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
interface Facility {
  name: string;
  icon: string;
}

interface FacilitiesModalProps {
  hotelName: string;
  facilities: Facility[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const FacilitiesModal: React.FC<FacilitiesModalProps> = ({
  hotelName,
  facilities,
  isOpen,
  setIsOpen,
}) => {
  // Group facilities by first letter
  const groupedFacilities = facilities.reduce(
    (acc: { [key: string]: Facility[] }, facility) => {
      const firstLetter = facility.name[0]?.toUpperCase();
      if (!firstLetter) return acc;
      if (!acc[firstLetter]) {
        acc[firstLetter] = [];
      }
      acc[firstLetter].push(facility);
      return acc;
    },
    {}
  );

  const sortedLetters = Object.keys(groupedFacilities).sort();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="max-w-[700px] max-h-[60vh] overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-light text-indigo-800 bg-indigo-100 rounded-md px-4 py-1 flex items-center gap-2">
              {hotelName}
            </p>
          </div>
          <DialogTitle className="text-xl font-semibold text-neutral-900">
            All Facilities
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div
          className="overflow-y-auto pr-2 pb-6 no-scrollbar"
          style={{ maxHeight: "calc(60vh - 140px)" }}
        >
          {sortedLetters.map((letter) => (
            <div key={letter} className="mb-6">
              <h3 className="text-sm font-semibold text-neutral-500 mb-3">
                {letter}
              </h3>
              <div className="grid grid-cols-2">
                {groupedFacilities[letter]?.map((facility, index) => {
                  const IconComponent =
                    iconMap[facility.icon as keyof typeof iconMap];

                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-neutral-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-neutral-200/50 rounded-full flex items-center justify-center">
                        {IconComponent ? (
                          <IconComponent className="w-4 h-4 text-neutral-600" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-neutral-600" />
                        )}
                      </div>
                      <span className="text-neutral-700 font-light text-sm">
                        {facility.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FacilitiesModal;
